import { CircularProgress, styled, useTheme } from '@mui/material';
import { UserTrainingStatistics } from 'models/UserTrainingStatistics';
import MUIDataTable from 'mui-datatables';
import { StyledTableContainer, StyledLoadingContainer } from 'components/StatisticsDataTable/StatisticsDataTable.styles';
import { useTranslation } from 'react-i18next';
import { convertToStatisticDataTableDto } from 'components/StatisticsDataTable/Utils';
import { useRewardTypeDictionaries } from 'components/StatisticsDataTable/hooks/useRewardTypeDictionaries';
import { DataTableStatisticDto } from 'components/StatisticsDataTable/Dto/DataTableStatisticDto';
import { OverridenGlobalStyles } from 'components/StatisticsDataTable/OverridenGlobalStyles';
import { buildStatisticsCsv, generateFileName } from 'components/StatisticsDataTable/csvUtils';
import { getStatisticsColumns } from 'components/StatisticsDataTable/Columns/StatisticTableColumns';
import { CustomStatisticsToolbar } from './CustomStatisticsToolbar';

interface UserTrainingStatisticsTableProps {
  data: UserTrainingStatistics[];
  gettingStatistics: boolean;
}

const StyledMUIDataTable = styled(MUIDataTable)`
  & .tss-1cdcmys-MUIDataTable-responsiveBase {
    overflow: visible !important;
  }
  & .MuiTableCell-head span button {
    font-size: 16px !important;
  }
`;

export const UserTrainingStatisticsTable = (props: UserTrainingStatisticsTableProps) => {
  const { data, gettingStatistics } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { rewardTypeDictionary, revertedRewardDictionary } = useRewardTypeDictionaries();

  const adjustedData: DataTableStatisticDto[] = convertToStatisticDataTableDto(data, rewardTypeDictionary);

  const columns = getStatisticsColumns({ t, theme, revertedRewardDictionary, adjustedData });

  return (
    <StyledTableContainer>
      {gettingStatistics ? (
        <StyledLoadingContainer>
          <CircularProgress />
        </StyledLoadingContainer>
      ) : (
        <OverridenGlobalStyles>
          <StyledMUIDataTable title={t('statistics.usersResult')} data={adjustedData} columns={columns} options={{
            textLabels: {
              body: {
                noMatch: t('dataTable.noMatch'),
                toolTip: t('dataTable.toolTip'),
                columnHeaderTooltip: column => t('dataTable.columnHeaderTooltip', { column: column.label })
              },
              pagination: {
                next: t('dataTable.next'),
                previous: t('dataTable.previous'),
                rowsPerPage: t('dataTable.rowsPerPage'),
                displayRows: t('dataTable.displayRows'),
              },
              toolbar: {
                search: t('dataTable.search'),
                downloadCsv: t('dataTable.downloadCsv'),
                print: t('dataTable.print'),
                viewColumns: t('dataTable.viewColumns'),
                filterTable: t('dataTable.filterTable'),
              },
              filter: {
                all: t('dataTable.all'),
                title: t('dataTable.title'),
                reset: t('dataTable.reset'),
              },
              viewColumns: {
                title: t('dataTable.viewColumnsTitle'),
                titleAria: t('dataTable.viewColumnsTitleAria'),
              },
              selectedRows: {
                text: t('dataTable.selectedRowsText'),
                delete: t('dataTable.delete'),
                deleteAria: t('dataTable.deleteAria'),
              },
          },
          fixedHeader: false,
          download: true,
          downloadOptions: {
          filename: generateFileName(),
            separator: ';',
            filterOptions: {
              useDisplayedColumnsOnly: true,
              useDisplayedRowsOnly: true
            }
          },
          onDownload: (_bh, _bb, cols, rows) => buildStatisticsCsv(t, cols, rows, { completedColumn: 'dateCompleted', pointsColumn: 'score' }),
          customToolbarSelect: (selectedRows, displayData, setSelectedRows) => (
            <CustomStatisticsToolbar
            selectedRows={selectedRows}
            displayData={displayData}
            adjustedData={adjustedData}
            columns={columns}
            t={t}
            buildStatisticsCsv={buildStatisticsCsv}
            generateFileName={generateFileName}
            meta={{ completedColumn: 'dateCompleted', pointsColumn: 'score' }}
            />
          ),
          onRowsDelete: () => false,      
          }}/>
        </OverridenGlobalStyles>
      )}
    </StyledTableContainer>
  );
};